<template>
    <div class="customer-record-container">
        <!-- 页面标题 -->
        <div class="page-header">
            <h1>客户购物菜品记录管理</h1>
        </div>

        <!-- 筛选查询区域 -->
        <div class="filter-panel">
            <el-card>
                <el-form :inline="true" :model="searchForm" class="search-form">
                    <!-- 客户信息查询 -->
                    <el-form-item label="客户姓名">
                        <el-input v-model="searchForm.customerName" placeholder="请输入客户姓名" clearable></el-input>
                    </el-form-item>

                    <el-form-item label="联系电话">
                        <el-input v-model="searchForm.phone" placeholder="请输入联系电话" clearable></el-input>
                    </el-form-item>

                    <!-- 订单信息查询 -->
                    <el-form-item label="订单编号">
                        <el-input v-model="searchForm.orderNo" placeholder="请输入订单编号" clearable></el-input>
                    </el-form-item>

                    <el-form-item label="菜品名称">
                        <el-input v-model="searchForm.dishName" placeholder="请输入菜品名称" clearable></el-input>
                    </el-form-item>

                    <!-- 时间范围查询 -->
                    <el-form-item label="购买时间">
                        <el-date-picker v-model="searchForm.dateRange" type="daterange" range-separator="至"
                            start-placeholder="开始日期" end-placeholder="结束日期" value-format="yyyy-MM-dd" unlink-panels
                            clearable></el-date-picker>
                    </el-form-item>

                    <!-- 订单状态筛选 -->
                    <el-form-item label="订单状态">
                        <el-select v-model="searchForm.status" placeholder="全部状态" clearable>
                            <el-option label="全部状态" value=""></el-option>
                            <el-option label="已完成" value="completed"></el-option>
                            <el-option label="已取消" value="cancelled"></el-option>
                            <el-option label="待支付" value="pending"></el-option>
                        </el-select>
                    </el-form-item>

                    <!-- 操作按钮 -->
                    <el-form-item>
                        <el-button type="primary" @click="handleSearch" icon="el-icon-search">查询</el-button>
                        <el-button @click="resetSearch" icon="el-icon-refresh">重置</el-button>
                        <el-button type="success" @click="exportData" icon="el-icon-download">导出数据</el-button>
                    </el-form-item>
                </el-form>
            </el-card>
        </div>

        <!-- 数据统计概览 -->
        <div class="stats-panel">
            <el-row :gutter="20">
                <el-col :span="6">
                    <el-card class="stat-card">
                        <div slot="header">
                            <span>总订单数</span>
                        </div>
                        <div class="stat-content">
                            <span class="stat-value">{{ totalOrders }}</span>
                            <span class="stat-desc">单</span>
                        </div>
                    </el-card>
                </el-col>
                <el-col :span="6">
                    <el-card class="stat-card">
                        <div slot="header">
                            <span>总销售额</span>
                        </div>
                        <div class="stat-content">
                            <span class="stat-value">¥{{ (totalSales || 0).toFixed(2) }}</span>
                            <span class="stat-desc">元</span>
                        </div>
                    </el-card>
                </el-col>
                <el-col :span="6">
                    <el-card class="stat-card">
                        <div slot="header">
                            <span>平均客单价</span>
                        </div>
                        <div class="stat-content">
                            <span class="stat-value">¥{{ (avgOrderAmount || 0).toFixed(2) }}</span>
                            <span class="stat-desc">元</span>
                        </div>
                    </el-card>
                </el-col>
                <el-col :span="6">
                    <el-card class="stat-card">
                        <div slot="header">
                            <span>总客户数</span>
                        </div>
                        <div class="stat-content">
                            <span class="stat-value">{{ totalCustomers }}</span>
                            <span class="stat-desc">人</span>
                        </div>
                    </el-card>
                </el-col>
            </el-row>
        </div>

        <!-- 记录列表 -->
        <div class="record-table">
            <el-card>
                <el-table :data="paginatedRecords" border stripe size="mini" style="width: 100%"
                    @row-click="handleRowClick" :row-class-name="tableRowClassName">
                    <el-table-column type="index" label="序号" width="60"></el-table-column>
                    <el-table-column prop="orderNo" label="订单编号" min-width="140"></el-table-column>
                    <el-table-column prop="customerName" label="客户姓名" min-width="100"></el-table-column>
                    <el-table-column prop="phone" label="联系电话" min-width="120"></el-table-column>
                    <el-table-column label="购买菜品" min-width="200">
                        <template #default="scope">
                            <div v-for="(dish, index) in scope.row.dishes" :key="index" class="dish-item">
                                {{ dish.name }}({{ dish.quantity }}份)
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column prop="totalAmount" label="订单金额" min-width="100">
                        <template #default="scope">
                            ¥{{ (scope.row.totalAmount || 0).toFixed(2) }}
                        </template>
                    </el-table-column>
                    <el-table-column prop="orderTime" label="购买时间" min-width="160" sortable></el-table-column>
                    <el-table-column prop="status" label="订单状态" min-width="100">
                        <template #default="scope">
                            <el-tag :type="statusTagType(scope.row.status)" size="small">
                                {{ statusText(scope.row.status) }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column label="操作" min-width="120" fixed="right">
                        <template #default="scope">
                            <el-button size="mini" type="text" @click="viewOrderDetail(scope.row)" icon="el-icon-view">
                                详情
                            </el-button>
                            <el-button size="mini" type="text" @click="handleRefund(scope.row)"
                                icon="el-icon-refresh-left" v-if="scope.row.status === 'completed'">
                                退款
                            </el-button>
                        </template>
                    </el-table-column>
                </el-table>

                <!-- 分页 -->
                <div class="pagination-container">
                    <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange"
                        :current-page="pagination.currentPage" :page-sizes="[10, 20, 50, 100]"
                        :page-size="pagination.pageSize" :total="filteredRecords.length"
                        layout="total, sizes, prev, pager, next, jumper"></el-pagination>
                </div>
            </el-card>
        </div>

        <!-- 订单详情弹窗 -->
        <el-dialog title="订单详情" :visible.sync="detailDialogVisible" width="60%" :close-on-click-modal="false">
            <div class="order-detail" v-if="currentOrder && currentOrder.id">
                <div class="detail-section">
                    <h3 class="section-title">订单基本信息</h3>
                    <el-descriptions column="1" border>
                        <el-descriptions-item label="订单编号">{{ currentOrder.orderNo }}</el-descriptions-item>
                        <el-descriptions-item label="客户姓名">{{ currentOrder.customerName }}</el-descriptions-item>
                        <el-descriptions-item label="联系电话">{{ currentOrder.phone }}</el-descriptions-item>
                        <el-descriptions-item label="购买时间">{{ currentOrder.orderTime }}</el-descriptions-item>
                        <el-descriptions-item label="订单状态">
                            <el-tag :type="statusTagType(currentOrder.status)">{{ statusText(currentOrder.status)
                                }}</el-tag>
                        </el-descriptions-item>
                        <el-descriptions-item label="支付方式">{{ currentOrder.payMethod }}</el-descriptions-item>
                        <el-descriptions-item label="备注信息">{{ currentOrder.remark || '无' }}</el-descriptions-item>
                    </el-descriptions>
                </div>

                <div class="detail-section">
                    <h3 class="section-title">购买菜品明细</h3>
                    <el-table :data="currentOrder.dishes" border size="mini" style="width: 100%">
                        <el-table-column prop="name" label="菜品名称"></el-table-column>
                        <el-table-column prop="price" label="单价" formatter="formatPrice"></el-table-column>
                        <el-table-column prop="quantity" label="数量"></el-table-column>
                        <el-table-column label="小计" formatter="formatSubtotal"></el-table-column>
                    </el-table>
                </div>

                <div class="detail-section total-amount-section">
                    <div class="total-amount">
                        订单总金额：<span class="amount-value">¥{{ (currentOrder.totalAmount || 0).toFixed(2) }}</span>
                    </div>
                </div>
            </div>
        </el-dialog>
    </div>
</template>

<script>
export default {
    data() {
        return {
            // 搜索表单数据
            searchForm: {
                customerName: '',
                phone: '',
                orderNo: '',
                dishName: '',
                dateRange: [],
                status: ''
            },

            // 分页配置
            pagination: {
                currentPage: 1,
                pageSize: 10
            },

            // 订单记录数据
            orderRecords: [
                {
                    id: 1,
                    orderNo: 'ORD20240601001',
                    customerName: '张三',
                    phone: '13800138000',
                    orderTime: '2024-06-01 12:30:45',
                    status: 'completed',
                    totalAmount: 88.5,
                    payMethod: '微信支付',
                    remark: '加辣',
                    dishes: [
                        { name: '宫保鸡丁', price: 38, quantity: 1 },
                        { name: '番茄蛋汤', price: 12.5, quantity: 2 },
                        { name: '米饭', price: 2, quantity: 3 }
                    ]
                },
                {
                    id: 2,
                    orderNo: 'ORD20240601002',
                    customerName: '李四',
                    phone: '13900139000',
                    orderTime: '2024-06-01 18:20:15',
                    status: 'completed',
                    totalAmount: 66,
                    payMethod: '支付宝',
                    remark: '',
                    dishes: [
                        { name: '鱼香肉丝', price: 42, quantity: 1 },
                        { name: '凉拌黄瓜', price: 10, quantity: 1 },
                        { name: '可乐', price: 6, quantity: 2 }
                    ]
                },
                {
                    id: 3,
                    orderNo: 'ORD20240602001',
                    customerName: '王五',
                    phone: '13700137000',
                    orderTime: '2024-06-02 11:45:30',
                    status: 'cancelled',
                    totalAmount: 58,
                    payMethod: '微信支付',
                    remark: '不要香菜',
                    dishes: [
                        { name: '水煮肉片', price: 48, quantity: 1 },
                        { name: '米饭', price: 2, quantity: 5 }
                    ]
                },
                {
                    id: 4,
                    orderNo: 'ORD20240602002',
                    customerName: '赵六',
                    phone: '13600136000',
                    orderTime: '2024-06-02 19:10:50',
                    status: 'pending',
                    totalAmount: 45,
                    payMethod: '未支付',
                    remark: '',
                    dishes: [
                        { name: '扬州炒饭', price: 22, quantity: 1 },
                        { name: '酸辣汤', price: 15, quantity: 1 },
                        { name: '卤蛋', price: 3, quantity: 2 }
                    ]
                },
                {
                    id: 5,
                    orderNo: 'ORD20240603001',
                    customerName: '张三',
                    phone: '13800138000',
                    orderTime: '2024-06-03 12:15:20',
                    status: 'completed',
                    totalAmount: 72,
                    payMethod: '微信支付',
                    remark: '',
                    dishes: [
                        { name: '回锅肉', price: 36, quantity: 1 },
                        { name: '炒时蔬', price: 18, quantity: 1 },
                        { name: '紫菜蛋汤', price: 10, quantity: 1 },
                        { name: '米饭', price: 2, quantity: 4 }
                    ]
                }
            ],

            // 筛选后的记录
            filteredRecords: [],

            // 分页后的记录
            paginatedRecords: [],

            // 详情弹窗
            detailDialogVisible: false,
            currentOrder: {}
        };
    },

    computed: {
        // 统计数据
        totalOrders() {
            return this.filteredRecords.length;
        },
        totalSales() {
            return this.filteredRecords.reduce((sum, item) => sum + (item.totalAmount || 0), 0);
        },
        avgOrderAmount() {
            const validOrders = this.filteredRecords.filter(item => item.totalAmount > 0);
            return validOrders.length > 0 ? this.totalSales / validOrders.length : 0;
        },
        totalCustomers() {
            const customerSet = new Set();
            this.filteredRecords.forEach(item => {
                if (item.phone) customerSet.add(item.phone);
            });
            return customerSet.size;
        }
    },

    created() {
        this.handleSearch(); // 初始化筛选和分页
    },

    methods: {
        // 处理搜索
        handleSearch() {
            this.pagination.currentPage = 1;
            this.applyFilters();
            this.paginateData();
        },

        // 应用筛选条件
        applyFilters() {
            let records = [...this.orderRecords];

            if (this.searchForm.customerName) {
                records = records.filter(record =>
                    record.customerName.includes(this.searchForm.customerName));
            }

            if (this.searchForm.phone) {
                records = records.filter(record =>
                    record.phone.includes(this.searchForm.phone));
            }

            if (this.searchForm.orderNo) {
                records = records.filter(record =>
                    record.orderNo.includes(this.searchForm.orderNo));
            }

            if (this.searchForm.dishName) {
                records = records.filter(record =>
                    record.dishes.some(dish => dish.name.includes(this.searchForm.dishName)));
            }

            if (this.searchForm.dateRange && this.searchForm.dateRange.length === 2) {
                const [startDate, endDate] = this.searchForm.dateRange;
                records = records.filter(record => {
                    const orderDate = record.orderTime.split(' ')[0];
                    return orderDate >= startDate && orderDate <= endDate;
                });
            }

            if (this.searchForm.status) {
                records = records.filter(record => record.status === this.searchForm.status);
            }

            this.filteredRecords = records;
        },

        // 分页处理
        paginateData() {
            const start = (this.pagination.currentPage - 1) * this.pagination.pageSize;
            const end = start + this.pagination.pageSize;
            this.paginatedRecords = this.filteredRecords.slice(start, end);
        },

        // 重置搜索
        resetSearch() {
            this.searchForm = {
                customerName: '',
                phone: '',
                orderNo: '',
                dishName: '',
                dateRange: [],
                status: ''
            };
            this.handleSearch();
        },

        // 导出数据
        exportData() {
            if (this.filteredRecords.length === 0) {
                this.$message.warning('没有可导出的数据');
                return;
            }
            this.$message.success('数据导出成功');
        },

        // 查看订单详情
        viewOrderDetail(order) {
            this.currentOrder = { ...order };
            this.detailDialogVisible = true;
        },

        // 处理退款
        handleRefund(order) {
            this.$confirm(`确定要为订单 ${order.orderNo} 办理退款吗?`, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                const index = this.orderRecords.findIndex(item => item.id === order.id);
                if (index !== -1) {
                    this.orderRecords[index].status = 'refunded';
                    this.handleSearch();
                }
                this.$message.success('退款操作已完成');
            }).catch(() => {
                this.$message.info('已取消退款');
            });
        },

        // 表格行点击事件
        handleRowClick(row) {
            this.viewOrderDetail(row);
        },

        // 表格行样式
        tableRowClassName({ row }) {
            if (row.status === 'cancelled' || row.status === 'refunded') {
                return 'row-cancelled';
            }
            return '';
        },

        // 状态文本转换
        statusText(status) {
            const statusMap = {
                'pending': '待支付',
                'completed': '已完成',
                'cancelled': '已取消',
                'refunded': '已退款'
            };
            return statusMap[status] || status;
        },

        // 状态标签样式
        statusTagType(status) {
            const typeMap = {
                'pending': 'warning',
                'completed': 'success',
                'cancelled': 'info',
                'refunded': 'danger'
            };
            return typeMap[status] || 'default';
        },

        // 格式化价格
        formatPrice(row, column, cellValue) {
            return `¥${(cellValue || 0).toFixed(2)}`;
        },

        // 格式化小计
        formatSubtotal(row) {
            return `¥${(row.price * row.quantity || 0).toFixed(2)}`;
        },

        // 分页大小改变
        handleSizeChange(val) {
            this.pagination.pageSize = val;
            this.paginateData();
        },

        // 当前页改变
        handleCurrentChange(val) {
            this.pagination.currentPage = val;
            this.paginateData();
        }
    }
};
</script>

<style lang="scss" scoped>
.customer-record-container {
    padding: 20px;
    background-color: #f5f7fa;
    min-height: 100vh;
    box-sizing: border-box;
}

.page-header {
    margin-bottom: 20px;

    h1 {
        font-size: 20px;
        color: #333;
        margin: 0;
        padding-bottom: 10px;
        border-bottom: 1px solid #eaeaea;
    }
}

.filter-panel {
    margin-bottom: 20px;

    .search-form {
        margin-top: 10px;

        .el-form-item {
            margin-right: 15px;
            margin-bottom: 15px;
        }
    }
}

.stats-panel {
    margin-bottom: 20px;

    .stat-card {
        height: 100%;

        .stat-content {
            padding: 15px 0;
            display: flex;
            align-items: baseline;

            .stat-value {
                font-size: 28px;
                font-weight: bold;
                color: #333;
                margin-right: 10px;
            }

            .stat-desc {
                color: #666;
                font-size: 14px;
            }
        }
    }
}

.record-table {
    .el-table {
        margin-bottom: 15px;
    }

    .dish-item {
        white-space: nowrap;
    }

    .row-cancelled {
        color: #999;
        background-color: #f9f9f9;
    }

    .pagination-container {
        text-align: right;
        margin-top: 15px;
    }
}

.order-detail {
    .detail-section {
        margin-bottom: 20px;

        .section-title {
            font-size: 16px;
            margin-bottom: 10px;
            color: #333;
            font-weight: 500;
        }
    }

    .total-amount-section {
        text-align: right;
        padding-top: 10px;
        border-top: 1px solid #eaeaea;

        .total-amount {
            font-size: 16px;

            .amount-value {
                font-size: 18px;
                font-weight: bold;
                color: #f56c6c;
                margin-left: 10px;
            }
        }
    }
}
</style>
